import React, { useCallback, useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';
import { FaPlus } from 'react-icons/fa';

import Radio from '../../../../../../newComponents/Radio';
import ComplementAccordion from '../../../../../../newComponents/ComplementAccordion';

import { useAuth } from '../../../../../../hooks/auth';
import { useToast } from '../../../../../../hooks/toast';
import { usePizzaGroup } from '../../../../../../hooks/pizzaGroup';
import {
  useComplements,
  Complement as IComplement,
} from '../../../../../../hooks/complements';

import ComplementsDrawer from '../../../Complements';

import ComplementsService from '../../../../../../services/ComplementsService';

import APIError from '../../../../../../errors/APIError';

import {
  Container,
  Header,
  Options,
  OptionContent,
  AddComplementsButton,
  ComplementsList,
  ComplementsListHeader,
  ComplementsListContainer,
} from './styles';

export const ComplementsFormSchema = Yup.object().shape({});

const Complements: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const {
    currentTab,
    complements,
    complementsDrawerTab,
    isComplementsDrawerOpened,
    handleChangeGroup,
    handleAddComplement,
    handleEditComplement,
    handleDeleteComplement,
    handleOpenComplementsDrawer,
    handleCloseComplementsDrawer,
    handleChangeComplementsDrawerTab,
  } = usePizzaGroup();
  const { handleChangeComplement } = useComplements();

  const [hasComplements, setHasComplements] = useState(false);

  const handleOnSave = useCallback(
    (complement: IComplement) => {
      if (complement?.id) {
        handleEditComplement(complement);
      } else {
        handleAddComplement({ ...complement, id: String(Math.random()) });
      }
    },
    [handleEditComplement, handleAddComplement],
  );

  const handleOnEdit = useCallback(
    (complement: IComplement) => {
      handleChangeComplement(complement);
      handleOpenComplementsDrawer();
    },
    [handleChangeComplement, handleOpenComplementsDrawer],
  );

  const handleOnDragEnd = useCallback(
    async (dropResult: DropResult) => {
      const { destination, source } = dropResult;

      if (
        typeof destination?.index === 'number' &&
        source.index !== destination.index &&
        complements
      ) {
        let sortedComplements = Array.from(complements);
        const [removedComplement] = sortedComplements.splice(source.index, 1);

        sortedComplements.splice(destination.index, 0, removedComplement);

        sortedComplements = sortedComplements.map(
          (complement, complementIndex) => ({
            ...complement,
            order: complementIndex + 1,
          }),
        );

        handleChangeGroup({ complements: sortedComplements });
      }
    },
    [complements, handleChangeGroup],
  );

  const handleOnDelete = useCallback(
    async (complement: IComplement) => {
      handleDeleteComplement(complement);

      if (complement && typeof complement.id === 'number') {
        try {
          await ComplementsService.deleteComplement(
            user.empresa_id,
            complement.id,
          );
        } catch (error) {
          if (error instanceof APIError) {
            addToast({
              title: error.message,
              type: 'error',
            });

            return;
          }

          addToast({
            title: 'Ocorreu um erro durante a operação',
            type: 'error',
          });
        }
      }
    },
    [user.empresa_id, addToast, handleDeleteComplement],
  );

  useEffect(() => {
    if (currentTab === 2 && complements && complements.length > 0) {
      setHasComplements(true);
    }

    if (!complements || !complements.length) {
      setHasComplements(false);
    }
  }, [currentTab, complements]);

  return (
    <Container>
      <Header>
        <h2>Complementos</h2>
        <p>Aqui você pode editar os complementos do seu grupo</p>
      </Header>
      <Options>
        <h4>Selecione uma opção</h4>
        <Radio
          checked={!hasComplements}
          value="false"
          name="hasComplements"
          defaultChecked
          onChange={() => setHasComplements(false)}
        >
          <OptionContent>
            <span>Não, este grupo não tem complementos</span>
          </OptionContent>
        </Radio>
        <Radio
          checked={hasComplements}
          value="true"
          name="hasComplements"
          onChange={() => setHasComplements(true)}
        >
          <OptionContent>
            <span>Sim, este grupo tem complementos</span>
          </OptionContent>
        </Radio>
      </Options>
      {!!complements && !!complements.length && hasComplements && (
        <ComplementsList>
          <ComplementsListHeader>
            <h2>Grupos de complementos</h2>
          </ComplementsListHeader>
          <DragDropContext
            onDragEnd={(dragResult) => handleOnDragEnd(dragResult)}
          >
            <Droppable droppableId="droppableComplements">
              {(droppableProvided, droppableStateSnapshot) => (
                <ComplementsListContainer
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {complements
                    .sort((a, b) => (a?.order ?? 1) - (b?.order ?? 1))
                    .map((complement, complementIndex) => (
                      <Draggable
                        key={`pizza-group-complement-${complementIndex}`}
                        draggableId={`pizza-group-complement-${complementIndex}`}
                        index={complementIndex}
                      >
                        {(draggableProvided) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              left: 'auto !important',
                            }}
                          >
                            <ComplementAccordion
                              complement={complement}
                              dragHandleProps={
                                draggableProvided.dragHandleProps
                              }
                              onEdit={() => handleOnEdit(complement)}
                              onDelete={() => handleOnDelete(complement)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {droppableProvided.placeholder}
                </ComplementsListContainer>
              )}
            </Droppable>
          </DragDropContext>
        </ComplementsList>
      )}
      {hasComplements && (
        <AddComplementsButton
          variant="outlined"
          onClick={handleOpenComplementsDrawer}
          type="button"
        >
          <FaPlus /> Criar grupo de complementos
        </AddComplementsButton>
      )}
      <ComplementsDrawer
        currentTab={complementsDrawerTab}
        isDrawerOpened={isComplementsDrawerOpened}
        onSave={handleOnSave}
        handleCloseDrawer={handleCloseComplementsDrawer}
        handleChangeTab={handleChangeComplementsDrawerTab}
        lastComplementIndex={
          complements && complements.length
            ? complements[complements.length - 1].order ?? 1
            : 1
        }
      />
    </Container>
  );
};

export default Complements;
